/**
 * Square Component
 * 
 * A simplified wrapper for rendering 2D squares with fixed optimal settings.
 * 
 * Only accepts dimension values (for labels), not positions.
 * All positioning and styling are predefined for consistency.
 * 
 * Uses SVG for rendering, similar to polygon rendering in GraphRenderer.
 */

import React from 'react';
import { Box } from '@mui/material';

interface SquareProps {
  side?: number; // Side length dimension (for label display)
  unit?: string; // Unit of measurement (default: 'cm')
  color?: string; // Fill color (default: '#e0e7ff')
  strokeColor?: string; // Stroke color (default: '#4f46e5')
}

/**
 * Square Component
 * 
 * Renders a 2D square with fixed, optimal positioning and labeling.
 * The square is always rendered at a normalized size for visual consistency.
 * The actual dimension value is only displayed in the label.
 * 
 * @param side - Side length dimension (default: 4)
 * @param unit - Unit of measurement (default: 'cm')
 * @param color - Fill color in any CSS format (default: '#e0e7ff')
 * @param strokeColor - Stroke color in any CSS format (default: '#4f46e5')
 */
export const Square: React.FC<SquareProps> = ({
  side = 4,
  unit = 'cm',
  color = '#e0e7ff',
  strokeColor = '#4f46e5'
}) => {
  // Fixed normalized size for visual consistency
  // Actual dimension values are only shown in labels
  const normalizedSize = 30; // Size in SVG units (viewBox is 0 0 100 100)
  
  // Center the square in the viewBox
  const centerX = 50;
  const centerY = 50;
  const halfSize = normalizedSize / 2;
  
  // Square corners
  const x1 = centerX - halfSize;
  const y1 = centerY - halfSize;
  const x2 = centerX + halfSize;
  const y2 = centerY + halfSize;
  
  // Label position (below the square)
  const labelX = centerX;
  const labelY = centerY + halfSize + 8;
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        {/* Square fill */}
        <rect
          x={x1}
          y={y1}
          width={normalizedSize}
          height={normalizedSize}
          fill={color}
          opacity={0.4}
        />
        
        {/* Square outline */}
        <rect
          x={x1}
          y={y1}
          width={normalizedSize}
          height={normalizedSize}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        
        {/* Corner points */}
        <circle cx={x1} cy={y1} r={1.2} fill={strokeColor} />
        <circle cx={x2} cy={y1} r={1.2} fill={strokeColor} />
        <circle cx={x2} cy={y2} r={1.2} fill={strokeColor} />
        <circle cx={x1} cy={y2} r={1.2} fill={strokeColor} />
        
        {/* Side length label */}
        <text
          x={labelX}
          y={labelY}
          fontSize={4.5}
          fill="#374151"
          textAnchor="middle"
          fontWeight={600}
        >
          s = {side} {unit}
        </text>
      </svg>
    </Box>
  );
};

export default Square;
